import { useState, useEffect, useRef, useMemo } from 'react';
import { useDocumentContext } from '../contexts/DocumentContext';
import { useManagerContext } from '../contexts/ManagerContext';
import { useEnhancedAuth } from '../contexts/EnhancedAuthContext';

const REMINDER_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const wasFieldReminderSentToday = (fieldId: string, userId: string): boolean => {
  try {
    const stored = localStorage.getItem(`field_reminder_${fieldId}_${userId}`);
    if (!stored) return false;
    return Date.now() - parseInt(stored, 10) < REMINDER_COOLDOWN_MS;
  } catch { return false; }
};
const markFieldReminderSent = (fieldId: string, userId: string): void => {
  try { localStorage.setItem(`field_reminder_${fieldId}_${userId}`, Date.now().toString()); } catch {}
};

interface UseDocumentFieldItemProps {
  field: any;
  value: any;
  isCompanyMode?: boolean;
}

interface FieldContextData {
  loading: boolean;
  selectedUser: any;
  sendUserFieldReminders: (fieldId: string, userId: string, managerId: string, message: string) => Promise<any>;
  userReminders: any[];
  removeUserFieldReminders: (fieldId: string, userId: string) => Promise<any>;
  fetchUserReminders: (userId: string) => Promise<any>;
  setExpandedCategoryId: (id: string | null) => void;
  setExpandedSubcategoryId: (id: string | null) => void;
  highlightedFieldId: string | null;
  setHighlightedFieldId: (id: string | null) => void;
}

/**
 * Custom hook to manage document field item state and logic
 * Extracts business logic from DocumentFieldItem component for better maintainability
 */
export const useDocumentFieldItem = ({ field, value, isCompanyMode = false }: UseDocumentFieldItemProps) => {
  const documentContext = useDocumentContext();
  const managerContext = useManagerContext();
  const { hasPermission, userDetails } = useEnhancedAuth();

  // State management
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [hasReminder, setHasReminder] = useState(false);
  const [reminderJustSent, setReminderJustSent] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Context selection based on mode - memoized to prevent dependency issues
  const contextData: FieldContextData = useMemo(() => isCompanyMode 
    ? {
        loading: managerContext?.loading || false,
        selectedUser: null,
        sendUserFieldReminders: () => Promise.resolve({}),
        userReminders: [],
        removeUserFieldReminders: () => Promise.resolve({}),
        fetchUserReminders: () => Promise.resolve([]),
        setExpandedCategoryId: () => {},
        setExpandedSubcategoryId: () => {},
        highlightedFieldId: null,
        setHighlightedFieldId: () => {}
      }
    : {
        loading: documentContext.loading,
        selectedUser: documentContext.selectedUser,
        sendUserFieldReminders: documentContext.sendUserFieldReminders,
        userReminders: documentContext.userReminders || [],
        removeUserFieldReminders: documentContext.removeUserFieldReminders,
        fetchUserReminders: documentContext.fetchUserReminders,
        setExpandedCategoryId: documentContext.setExpandedCategoryId,
        setExpandedSubcategoryId: documentContext.setExpandedSubcategoryId,
        highlightedFieldId: documentContext.highlightedFieldId,
        setHighlightedFieldId: documentContext.setHighlightedFieldId
      }, [
        isCompanyMode,
        managerContext,
        documentContext.loading,
        documentContext.selectedUser,
        documentContext.sendUserFieldReminders,
        documentContext.userReminders,
        documentContext.removeUserFieldReminders,
        documentContext.fetchUserReminders,
        documentContext.setExpandedCategoryId,
        documentContext.setExpandedSubcategoryId,
        documentContext.highlightedFieldId,
        documentContext.setHighlightedFieldId
      ]);

  // Check for reminders
  useEffect(() => {
    const fieldReminders = (contextData.userReminders as any)?.fieldReminders;
    if (Array.isArray(fieldReminders) && field && field.id) {
      const reminderExists = fieldReminders.some((reminder: any) =>
        reminder.field_type_id === field.id
      );
      setHasReminder(reminderExists);
    } else {
      // If no reminders data or no field, set to false
      setHasReminder(false);
    }
  }, [(contextData.userReminders as any)?.fieldReminders, field]);

  // Reset reminderJustSent when selected user changes
  useEffect(() => {
    setReminderJustSent(false);
  }, [contextData.selectedUser?.id]);

  // Handle highlighting
  useEffect(() => {
    if (contextData.highlightedFieldId === field.id) {
      setIsHighlighted(true);
    }
  }, [contextData.highlightedFieldId, field.id]);

  // Fade out highlight after timeout and scroll into view
  useEffect(() => {
    let fadeTimeout: ReturnType<typeof setTimeout>;
    let removeTimeout: ReturnType<typeof setTimeout>;
    
    if (isHighlighted && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      fadeTimeout = setTimeout(() => {
        setIsFading(true);
        removeTimeout = setTimeout(() => {
          setIsHighlighted(false);
          setIsFading(false);
          contextData.setHighlightedFieldId(null);
          contextData.setExpandedCategoryId(null);
          contextData.setExpandedSubcategoryId(null);
        }, 1000); // fade duration
      }, 2000); // highlight duration
    }
    
    return () => {
      clearTimeout(fadeTimeout);
      clearTimeout(removeTimeout);
    };
  }, [isHighlighted, contextData]);

  // Handle send reminder
  const handleSendReminder = async (fieldID: string) => {
    if (!contextData.selectedUser || !userDetails) return;
    if (wasFieldReminderSentToday(fieldID, contextData.selectedUser.id)) return;
    setProcessing(true);
    setReminderJustSent(true);
    try {
      await contextData.sendUserFieldReminders(
        fieldID,
        contextData.selectedUser.id,
        userDetails.id,
        `Please complete the field: ${field.name}`
      );
      if (contextData.selectedUser?.id && typeof contextData.fetchUserReminders === 'function') {
        await contextData.fetchUserReminders(contextData.selectedUser.id);
      }
      markFieldReminderSent(fieldID, contextData.selectedUser.id);
    } catch (error) {
      console.error('Failed to send reminder:', error);
      setReminderJustSent(false);
    } finally {
      setProcessing(false);
    }
  };

  // Permission checks
  // For company mode: check if user's access level allows editing this field type
  // For user mode: user can edit their own fields if not archived
  const canEdit = useMemo(() => {
    if (isCompanyMode) {
      // Use native column names: edit_role_id
      const roles = managerContext?.roles || [];
      const roleById = new Map(roles.map((r: any) => [r.id, r]));
      
      if (field.edit_role_id && userDetails?.accessLevel !== undefined) {
        const editRole = roleById.get(field.edit_role_id);
        if (editRole && editRole.access_level !== undefined) {
          return userDetails.accessLevel <= editRole.access_level;
        }
      }
      return false;
    } else {
      // User mode: users can only edit their own fields
      return userDetails && contextData.selectedUser && 
        userDetails.id === contextData.selectedUser.id && 
        !contextData.selectedUser.archived;
    }
  }, [isCompanyMode, field.edit_role_id, managerContext?.roles, userDetails?.accessLevel, userDetails?.id, contextData.selectedUser?.id, contextData.selectedUser?.archived]);

  const canManageUsers = hasPermission('user_manage_agency');
  const isViewingOwnDocuments = userDetails && contextData.selectedUser && userDetails.id === contextData.selectedUser.id;
  const isViewingOtherUser = canManageUsers && contextData.selectedUser && userDetails && userDetails.id !== contextData.selectedUser.id;
  const isMissing = value === undefined || value === null || value === '';
  
  // Show bell icon ONLY when viewing own documents and a reminder exists
  const showReminderIndicator = isViewingOwnDocuments && hasReminder;

  const reminderSentToday = useMemo(() => {
    if (!contextData.selectedUser?.id || !field?.id) return false;
    return wasFieldReminderSentToday(field.id, contextData.selectedUser.id);
  }, [contextData.selectedUser?.id, field?.id, reminderJustSent]);
  
  // Show send reminder button ONLY when viewing other user's documents, field is missing, AND no reminder exists
  const showSendReminderButton = isViewingOtherUser && isMissing && !hasReminder;
  
  // Show "reminder sent" indicator when viewing other user's documents and a reminder already exists
  const showReminderSentIndicator = isViewingOtherUser && hasReminder;

  // Format value based on field type
  const getFormattedValue = () => {
    if (field.fieldType === 'date' && value) {
      return new Date(value).toISOString().split('T')[0];
    }
    return value || '';
  };

  return {
    // State
    isHighlighted,
    isFading,
    processing,
    reminderJustSent,
    reminderSentToday,
    containerRef,
    
    // Context data
    loading: contextData.loading,
    
    // Handlers
    handleSendReminder,
    
    // Permission/UI flags
    canEdit,
    showReminderIndicator,
    showSendReminderButton,
    showReminderSentIndicator,
    
    // Computed values
    formattedValue: getFormattedValue(),
  };
};
